import {
  AemVariableConfig,
  APP_TYPE_ANY,
  ArgumentObjType,
  ERROR_CODES,
  ErrorMessage,
  KeyValue,
  ServiceAddonType,
  SpecialPromo
} from '../types/common.types';
import { Checkout } from '../types/checkout.types';
import { DeliveryState } from '../types/fulfilment.types';
import FULFILMENT_CONSTANTS from '../constants/fulfillment';
import CHECKOUT_CONSTANTS from '../constants/checkout';
import { UserInformation } from '../types/user.types';
import {
  BaseAddress,
  BillingAccountDetail
} from '../types/registrationCheckout';
import { getUrlParams } from '@lux/helpers';
import { ProductOrder } from '../types/orderSummary.types';
import { AmendedTabErrorData } from '../types/order';
import { COMMON } from '../constants';
import CONSTANTS, { TRANSACTION_TYPE, USER_FLOW } from '../constants/common';
import { trans as t } from './localisation';
import { simCardType } from '../config/simcard-types';
import { api, config } from '@detox/actions';
import sleep from './sleep';
import rrpHeadersPages, { rrpIppPages } from '../constants/rrpHeadersPages';
import { AEM_CONFIG_KEYS, CONFIGS } from '../config/common';
import { navigation } from '../middlewares/navigation-constants';
import { BTLActionLayoutModel, FLOW, Plan } from '../types/plan.types';
import { Plan as BTLOfferPlan } from '../types/btloffer.types';
import { PretickAddon } from '../types/addons.types';
import LINKS from '../config/links';
import { parsePlanDescription } from '@common-modules/product-catalogue';
import CONSTANT from '../constants/common';
import { deleteCookie } from './set-cookie';
import { getCookie } from '@lux/helpers';
import { getDurationLiner } from './btl-offer-normalizer';
import { NUMBER_SELECTION_TAB_ITEMS } from '../config/modals';

const revokeTokenApi = api?.onlineApi?.revokeToken;
declare global {
  interface Window {
    dataLayer: APP_TYPE_ANY;
    ga: APP_TYPE_ANY;
  }
}

const getErrorMessage = (
  errorCode: ERROR_CODES,
  allErrors: ErrorMessage[] = []
): ErrorMessage => {
  return allErrors.find(error => errorCode === error.errorCode);
};

export const maskString = (
  str: string | number,
  length = 4,
  type: 'HEAD' | 'TAIL' = 'HEAD'
): string => {
  if (!str) return '';
  const maskedChars = Array(length)
    .fill('*')
    .join('');
  if (typeof str === 'number') {
    str = str.toString();
  }
  if (type === 'HEAD') {
    const unmaskedString = str.substr(length);
    return `${maskedChars}${unmaskedString}`;
  } else {
    const unmaskedString = str.substr(0, str.length - length);
    return `${unmaskedString}${maskedChars}`;
  }
};

const arrayUnique = (values: string[]): string[] => {
  return [...new Set(values)].filter(Boolean);
};

export { getErrorMessage, arrayUnique };

export const filterBillingAddress = (
  billingAccountDetails: BillingAccountDetail[] = []
): BillingAccountDetail[] => {
  return billingAccountDetails?.filter(
    item =>
      item?.baStatus === config?.billingAccountStatus?.OPEN ||
      item?.baStatus === config?.billingAccountStatus?.TENTATIVE
  );
};
export const getAccountBillingInfo = ({
  checkout,
  delivery,
  userInformation,
  productOrder,
  newBarId
}: {
  checkout: Checkout;
  delivery: DeliveryState;
  userInformation?: UserInformation;
  productOrder?: ProductOrder;
  newBarId?: string;
}): {
  barId: string;
  billingMethod: string;
  faId: string;
  customerId: string;
} => {
  const defaultCustomerId =
    productOrder?.service?.customerId ||
    userInformation?.clientContext?.customers?.[0]?.customerId ||
    userInformation?.anonymousCustomerId;
  let barId,
    billingMethod,
    customerId = defaultCustomerId;
  const newBillingAccountId =
    newBarId ||
    (checkout.checkoutFormData?.billingAddress ===
      CHECKOUT_CONSTANTS.NEW_BILLING_ADDRESS_ID &&
      checkout.billingAccountId);
  const billingAccountDetails = filterBillingAddress(
    delivery?.deliveryDetails?.billingAccountDetails
  );
  if (newBillingAccountId) {
    // user added new billing address
    barId = newBillingAccountId;
    billingMethod =
      checkout?.checkoutFormData?.billingPreference === 0
        ? FULFILMENT_CONSTANTS?.BILLING_METHODS.NONE_PAPER
        : FULFILMENT_CONSTANTS.BILLING_METHODS.PAPER;
  } else {
    const selectedExistBillingAccountIndex =
      checkout.checkoutFormData?.billingAddress;
    if (selectedExistBillingAccountIndex !== undefined) {
      //  user select billing
      const selectedBillingAccountDetails =
        billingAccountDetails?.[selectedExistBillingAccountIndex];
      barId = selectedBillingAccountDetails?.billingAccountNumber;
      billingMethod = selectedBillingAccountDetails?.billDeliveryMethod;
      customerId =
        selectedBillingAccountDetails?.customerId || defaultCustomerId;
    } else if (
      billingAccountDetails &&
      billingAccountDetails.length > 0 &&
      productOrder?.service?.serviceId
    ) {
      const selectedBillingAccount = billingAccountDetails.filter(
        billingAccount => {
          const subscriberList = billingAccount?.subscribers;
          return (
            subscriberList?.length > 0 &&
            subscriberList.find(
              subscriberItem =>
                subscriberItem?.subscriberResourceVal ===
                  productOrder?.service?.serviceId ||
                subscriberItem?.serviceId === productOrder?.service?.serviceId
            ) /*In eshop billings, we get serviceId instead of subscriberResourceVal. This is a temp fix until we have MCSS*/
          );
        }
      );
      /** BarId always consider the selected billing account details **/
      barId = selectedBillingAccount?.[0]?.billingAccountNumber;
      billingMethod = selectedBillingAccount?.[0]?.billDeliveryMethod;
      customerId = selectedBillingAccount?.[0]?.customerId;
    } else {
      barId =
        billingAccountDetails?.[0]?.billingAccountNumber ||
        userInformation?.clientContext?.billingArrangements?.[0]?.barId;
    }
  }
  const faId =
    billingAccountDetails?.find(ba => ba.billingAccountNumber === barId)
      ?.faId || userInformation?.clientContext?.financialAccounts?.[0].faId;
  return {
    barId,
    billingMethod,
    faId,
    customerId
  };
};

export const getDisplayUnitNumber = ({
  unitNumber,
  floor
}: {
  unitNumber: number | string;
  floor: number | string;
}): string => {
  if (!unitNumber) return '';
  if (typeof unitNumber === 'number') {
    unitNumber = unitNumber.toString();
  }

  if (typeof floor === 'number') {
    floor = floor.toString();
  }
  if (unitNumber.includes('#')) {
    return unitNumber;
  }
  if (floor && floor.length === 1) {
    floor = `0${floor}`;
  }
  if (unitNumber && unitNumber.length === 1) {
    unitNumber = `0${unitNumber}`;
  }
  return `#${floor}-${unitNumber}`;
};

export const getDisplayAddress = (
  address: BaseAddress,
  hasPostalCode = true
): APP_TYPE_ANY => {
  if (!address) return '';
  const unitNumber = address?.unitNumber || address?.apartment;
  return `${
    address?.blockOrHouseNum || address?.buildingNo
      ? `${address?.blockOrHouseNum || address?.buildingNo} `
      : ''
  }${address?.streetName ? `${address?.streetName} ` : ''}${
    address?.buildingName ? `${address?.buildingName} ` : ''
  }${getDisplayUnitNumber({
    floor: address?.floor,
    unitNumber
  })}${hasPostalCode ? ` ${address?.postcode}` : ''}`;
};

export const formatContactNumber = (contactNo: APP_TYPE_ANY): APP_TYPE_ANY => {
  if (contactNo?.indexOf('+') >= 0) return contactNo;
  else {
    const splitNo = contactNo?.split('');
    if (splitNo?.length) {
      splitNo?.splice(4, 0, ' ');
      return `+65 ${splitNo.join('')}`;
    }
    return null;
  }
};

export const getQueryData = (
  location: Partial<Location>,
  paramKey: string
): APP_TYPE_ANY => {
  const queryParams = getUrlParams(location?.search);
  return queryParams[paramKey];
};

export const getErrorMessageFromQueryParam = ({
  location,
  paramKey,
  errorData = []
}: {
  location: Partial<Location>;
  paramKey: string;
  errorData: ErrorMessage[];
}): Partial<ErrorMessage> => {
  const errorCodeValue = getQueryData(location, paramKey);
  return (
    errorData.find(
      ({ errorCode, bccErrorCode }) =>
        (errorCode && errorCode === errorCodeValue) ||
        (bccErrorCode && errorCodeValue === bccErrorCode)
    ) || {}
  );
};

export const getFormattedDisplayAddress = (
  address: BaseAddress
): APP_TYPE_ANY => {
  if (!address) return '';
  const unitNumber = address.unitNumber || address.apartment;
  const blockOrHouseNumber = address?.blockOrHouseNum || address?.houseNumber;
  return [
    {
      text: `${blockOrHouseNumber ? `${blockOrHouseNumber} ` : ''}${
        address?.streetName ? `${address.streetName} ` : ''
      }${address.buildingName ? `${address.buildingName} ` : ''}`
    },
    {
      text: getDisplayUnitNumber({
        floor: address.floor,
        unitNumber
      }),
      nowrap: true
    }
  ];
};

export const isValidUrl = (url?: string): boolean => {
  if (!url) {
    return false;
  }
  let givenURL;
  try {
    givenURL = new URL(url);
  } catch (error) {
    return false;
  }
  return givenURL.protocol === 'http:' || givenURL.protocol === 'https:';
};

export const composeRequestParams = (params: KeyValue = {}): APP_TYPE_ANY => {
  const paramArray = Object.keys(params).filter(key => Boolean(params[key]));
  return paramArray.reduce((result, key, index) => {
    result = `${result}${key}=${params[key]}${
      index < paramArray.length - 1 ? '&' : ''
    }`;
    return result;
  }, '?');
};

export const includes = (array: string[], text: string): boolean => {
  return !!array?.some(string => {
    return string.trim().toUpperCase() === text?.trim()?.toUpperCase();
  });
};

export const isTabAmendedFlow = (
  amendedTabErrorData: AmendedTabErrorData
): APP_TYPE_ANY => {
  return (
    amendedTabErrorData?.errorUUID &&
    amendedTabErrorData?.backendErrorInfo?.includes(
      COMMON.AMENDED_IN_OTHER_FLOW_ERROR
    )
  );
};

export const deepCopy = (data: KeyValue): APP_TYPE_ANY => {
  return JSON.parse(JSON.stringify(data));
};

export const getShortenUrl = (url = '', includeUrlParams = true): string => {
  const splitUrl = url.split('/');
  const shortenURl = splitUrl[splitUrl.length - 1];
  if (includeUrlParams) {
    return shortenURl;
  }
  return shortenURl.split('?')[0];
};

export function formatNumber(
  value: number,
  targetLength = 2,
  padString = '0'
): string {
  return String(value).padStart(targetLength, padString);
}

type GetFirstEntriesReturn =
  | { [key: string]: APP_TYPE_ANY }
  | APP_TYPE_ANY[]
  | undefined;

export const getFirstEntries = <T>(
  data: T,
  numberOfEntriesToGet: number
): GetFirstEntriesReturn => {
  if (typeof data !== 'object') {
    return undefined;
  }

  if (Array.isArray(data)) {
    return data.slice(0, numberOfEntriesToGet);
  }

  return Object.fromEntries(
    Object.entries(data).slice(0, numberOfEntriesToGet)
  );
};

export const isValidPhoneNumber = (number: string): boolean => {
  const exp = /^\+65 [89][0-9]{3} [0-9]{4}$/;
  return exp.test(number);
};

/**
 *
 * @param value string
 * @param invertCheck to check if values is not empty
 * @returns boolean true or false
 */
export const isNullOrUndefinedOrEmptyCheck = (
  value: string,
  invertCheck = false
): boolean => {
  if (!invertCheck) {
    return value === null || value === undefined || value.trim() === '';
  }

  return value !== null && value !== undefined && value.trim() !== '';
};

export const getAdditionalBillDescription = (description = ''): string => {
  if (
    description.includes(simCardType.simType5G) ||
    description.includes(simCardType.simType4G)
  ) {
    return t('PHYSICAL_SIM_CARD_FEE') as string;
  } else if (description.includes(simCardType.simTypeESim)) {
    return t('ESIM_FEE') as string;
  }

  return description;
};

export const getErrorResponseHeaders = async (
  response: Response,
  callback?: (error: Error) => void
): Promise<string | null> => {
  try {
    return response.headers.get('x-idpf-verify-error');
  } catch (error) {
    if (callback) {
      callback(error);
    }
    return null;
  }
};

export const isErrorMatchFound = (idpfVerifyError: string): boolean => {
  if (!idpfVerifyError) return false;
  const normalizedError = idpfVerifyError?.toUpperCase().replace(/ /g, '');
  return CONSTANTS.IDPF_VERIFY_ERRORS.some(item =>
    item.includes(normalizedError)
  );
};

export const isApigeeAuthFailure = (
  errorStatus: number,
  errorFault: string
): boolean => {
  return (
    errorStatus === CONSTANTS.AUTH_UNAUTHORIZED &&
    CONSTANTS.AUTH_FAULT_ERRORS.some(error =>
      error.includes(errorFault?.toUpperCase()?.replace(/ /g, ''))
    )
  );
};

export const flattenNodes = (arr: APP_TYPE_ANY): APP_TYPE_ANY => {
  return arr?.edges?.map(({ node }) => node);
};

export const dataLayerPush = (
  payload: APP_TYPE_ANY,
  resetEcommerceNeeded?: APP_TYPE_ANY
): void => {
  if (window.dataLayer) {
    if (resetEcommerceNeeded) {
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(payload);
  }
};

/**
 * Split the phone number into groups of 4 digits
 *
 * @param {string|number} number
 * @returns {string}
 */
export const splitNumber = (number: number | string): string =>
  String(number)
    .match(/.{4}/g)
    .join(' ');

/**
 * Check and return if an object is empty
 *
 * @param {Object} obj
 * @returns {Boolean}
 */
export const isEmpty = (obj: APP_TYPE_ANY): boolean => {
  return obj ? Object.keys(obj).length === 0 : true;
};

export const gaSendEvent = (options: APP_TYPE_ANY): void => {
  if (window.ga) {
    window.ga('send', 'event', options);
  }
};

export const fetchRetry = async (
  url: string,
  fetchOptions = {},
  retryDelay = 1000
): Promise<APP_TYPE_ANY> => {
  await sleep(retryDelay);
  return fetch(url, fetchOptions).then(res => res.json());
};

/**
 *
 * @param {*} arr Stack
 * @param {*} needle Find tag
 */
export const isTagged = (arr: APP_TYPE_ANY[], needle: string): boolean => {
  if (!arr) return false;

  return arr.some(item => item.toLowerCase().includes(needle.toLowerCase()));
};

export const isRRPPage = (location: Location, isIpp = false): boolean => {
  return (isIpp ? rrpIppPages : rrpHeadersPages).reduce(
    (result: boolean, pageName) => {
      if (!result && location.pathname.includes(pageName)) {
        result = true;
      }
      return result;
    },
    false
  );
};

export const getAemConfig = (
  configs: AemVariableConfig[] = [],
  key: AEM_CONFIG_KEYS
): string => {
  return configs.find(({ name }) => name === key)?.value;
};

export const getAemConfigs = (
  configs: AemVariableConfig[] = [],
  keys: AEM_CONFIG_KEYS[]
): string[] => {
  return keys.map(name => configs.find(config => config.name === name)?.value);
};

export function extractBasePath(url) {
  const protocolIndex = url.indexOf('://');
  if (protocolIndex !== -1) {
    const afterProtocol = url.substring(protocolIndex + 3);
    const domainEndIndex = afterProtocol.indexOf('/');
    if (domainEndIndex !== -1) {
      return url.substring(0, protocolIndex + 3 + domainEndIndex);
    } else {
      // URL has no path
      return url;
    }
  } else {
    // No protocol found, return null
    return null;
  }
}

/**
 * Validates if a URL has a safe protocol (http or https)
 * @param url - The URL to validate
 * @returns true if URL has http or https protocol, false otherwise
 */
export function isValidUrlProtocol(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
}

/**
 * Validates if a URL belongs to an allowed domain
 * @param url - The URL to validate
 * @param allowedDomains - Array of allowed domains (e.g., ['singtel.com', 'shop.singtel.com'])
 * @returns true if URL hostname is in allowed domains, false otherwise
 */
export function isAllowedDomain(
  url: string,
  allowedDomains: string[] = ['singtel.com']
): boolean {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname;

    if (!hostname) {
      return false;
    }

    return allowedDomains.some(domain => {
      return (
        hostname === domain ||
        (hostname.endsWith('.' + domain) && hostname.length > domain.length + 1)
      );
    });
  } catch {
    return false;
  }
}

/**
 * Comprehensive URL validation for security
 * @param url - The URL to validate
 * @param allowedDomains - Array of allowed domains
 * @returns true if URL passes all security checks, false otherwise
 */
export function isSecureRedirectUrl(
  url: string,
  allowedDomains: string[] = ['singtel.com']
): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return isValidUrlProtocol(url) && isAllowedDomain(url, allowedDomains);
}

export const removeUndefinedFields = (datalayer: KeyValue = {}): KeyValue => {
  return Object.entries(datalayer).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
    return result;
  }, {});
};

export const isQualtricsPage = (pagePath: string) => {
  return pagePath === `/${navigation.THANK_YOU}`;
};

export const getSpecialPromos = (specialPromoAemData = ''): SpecialPromo[] => {
  const specialPromoStringArr = specialPromoAemData
    .replace(/\s+/g, '')
    .split(',');
  const specialPromos: SpecialPromo[] = specialPromoStringArr.reduce(
    (result: SpecialPromo[], specialPromoString) => {
      const [
        promoCode,
        planIdStr,
        boIdStr,
        pretickBoIdStr,
        showPhysicalSimStr
      ] = specialPromoString.split('|');
      if (promoCode) {
        result.push({
          promoCode,
          planIds: planIdStr ? planIdStr.split(':') : [],
          boIds: boIdStr ? boIdStr.split(':') : [],
          pretickBoIds: pretickBoIdStr ? pretickBoIdStr.split(':') : [],
          showPhysicalSim: showPhysicalSimStr === '1' // treat '1' as true, else false
        });
      }

      return result;
    },
    []
  );
  return specialPromos;
};

export const getSpecialPromo = ({
  urlParams = {},
  specialPromoAemData = ''
}: {
  urlParams: KeyValue;
  specialPromoAemData: string;
}): { status: boolean; specialPromo: SpecialPromo } => {
  const specialPromos = getSpecialPromos(specialPromoAemData);
  const planRelId = urlParams[CONSTANTS.URL_PARAMS.PLAN_ID];
  const promoteCode = urlParams[CONSTANTS.URL_PARAMS.PROMOTE_CODE];
  const eligiblePromo = specialPromos.find(
    promo => promo.promoCode === promoteCode
  );

  if (CONFIGS.SPECIAL_PROMOTE_NO_CODES.includes(eligiblePromo?.promoCode)) {
    eligiblePromo.promoCode = null;
  }

  return {
    status: !!(planRelId && eligiblePromo?.planIds?.includes(planRelId)),
    specialPromo: eligiblePromo
  };
};

export const getSpecialPretickAddons = ({
  selectedPlan,
  specialPromo
}: {
  selectedPlan: Plan;
  specialPromo: SpecialPromo;
}): PretickAddon => {
  if (!specialPromo?.pretickBoIds?.length) {
    return null;
  }
  return {
    boIds: specialPromo?.pretickBoIds || [],
    planIds: [selectedPlan.planId],
    transactionSpecific: false,
    phoneSpecific: false,
    segment: [USER_FLOW.CIS, USER_FLOW.PUBLIC, USER_FLOW.STAFF],
    id: ''
  };
};

export const getExcludePreTickAddons = (
  isSpecialPromoEnabled,
  allAddonPreTick,
  specialPromo,
  selectedPlan
) => {
  if (isSpecialPromoEnabled && specialPromo) {
    const excludeBoids = specialPromo?.boIds;
    const excludePreTickAddons = allAddonPreTick.filter(item => {
      return (
        (!item.boIds ||
          !item.boIds.some(boId => excludeBoids.includes(boId))) &&
        (!item.spsIds ||
          !item.spsIds.some(spsId => excludeBoids.includes(spsId)))
      );
    });
    // Return the exluded pretick addon list
    const specialPretickAddon = getSpecialPretickAddons({
      selectedPlan,
      specialPromo
    });
    if (specialPretickAddon) {
      return [...excludePreTickAddons, specialPretickAddon];
    }
    return excludePreTickAddons;
  }
  // Return original pretick addon list if no exclude boid is needed

  return allAddonPreTick;
};

export const getSkipAddonsFromConfig = (
  skipAddonsConfig: string,
  selectedPlan: string,
  specialPromo: SpecialPromo
): boolean => {
  if (!skipAddonsConfig || !selectedPlan) {
    return false;
  }
  const [skipAddonByPlanIdStr, skipAddonByCampaignStr] = skipAddonsConfig.split(
    ','
  );
  const [skipAddons, skipAddonsPlans] = skipAddonByPlanIdStr.split(':');
  const configuredPlans = skipAddonsPlans?.split('|') || [];

  let isSkippedByCampaign = false;
  if (specialPromo && skipAddonByCampaignStr) {
    const [skipByCampaignStr, campaignsStr] = skipAddonByCampaignStr.split(':');
    const campaigns = campaignsStr?.split('|') || [];
    isSkippedByCampaign =
      skipByCampaignStr === 'true' &&
      campaigns.includes(specialPromo.promoCode);
  }

  return (
    (skipAddons === 'true' && configuredPlans.includes(selectedPlan)) ||
    isSkippedByCampaign
  );
};

export const getHasedUserServiceSubID = (
  infoState: ArgumentObjType,
  selectedService: ArgumentObjType
): string => {
  if (!selectedService) {
    return '';
  }
  const subscriptionData = (
    infoState.information?.clientContext?.contactSubscriptionDetails || []
  ).find(
    contactSubscription =>
      +contactSubscription.subscriberId === selectedService?.subscriptionId
  );
  return subscriptionData?.hashedPrimaryResource || '';
};

export function openTargetUrlAsync(url) {
  setTimeout(() => {
    window.open(url, '_self');
  }, 600);
}

export function openLinkInNewTab(link = LINKS.whatsAppChatLink) {
  window.open(link, '_blank', 'noopener,noreferrer');
}

export const isEkycError = (ekycState, urlParams) => {
  const { ekycFlag } = ekycState?.verifyEkycPayload ?? {};
  const isEkycApiError =
    ekycState?.isVerifyEkycError ||
    ekycState?.isInitEkycError ||
    ekycState?.initEkycpayload === ERROR_CODES.ERROR_RESPONSE;
  const isEkycModuleError = urlParams?.ref === ERROR_CODES.EKYC_REF_ERROR_CODE;

  return ekycFlag === 'true' || isEkycApiError || isEkycModuleError;
};

/**
 * Utility to map productId and variantId based on transactionType.
 * @param {Array} data - The input array.
 * @param {'RECON'|'PORT-IN'|'NEW'} transactionType
 * @returns {Array<{productId: string, variantId: string}>}
 */
interface PxAddonItem {
  productId: string;
  variationIds: string[] | null;
  transactionSpecific?: boolean;
  transactionVariationIds?: {
    reconVariationIds?: string[] | null;
    portinVariationIds?: string[] | null;
    gaVariationIds?: string[] | null;
    [key: string]: string[] | null | undefined;
  };
  segment?: string[];
  planIds?: string[];
  phoneSpecific?: boolean;
  phoneList?: string[];
}

export const getPxAddonsProductVariantMap = (
  preTickPxAddons: PxAddonItem[] = [],
  transactionType: TRANSACTION_TYPE = TRANSACTION_TYPE.NEW,
  customerType = FLOW.PUBLIC,
  planId = '',
  selectedProduct = ''
): Array<{ productId: string; variantId: string }> => {
  const typeMap = {
    [TRANSACTION_TYPE.RECON]: 'reconVariationIds',
    [TRANSACTION_TYPE.PORT_IN]: 'portinVariationIds',
    [TRANSACTION_TYPE.NEW]: 'gaVariationIds',
    [TRANSACTION_TYPE.MS_NEW]: 'gaVariationIds',
    [TRANSACTION_TYPE.MS_RECONTRACT]: 'reconVariationIds'
  };

  // 1. Filter by segment (customerType)
  let filteredPreTicks = preTickPxAddons.filter(addon => {
    if (!addon.segment || !Array.isArray(addon.segment)) return false;
    return addon.segment
      .map(s => (typeof s === 'string' ? s.trim().toLowerCase() : ''))
      .includes(
        typeof customerType === 'string'
          ? customerType.trim().toLowerCase()
          : ''
      );
  });

  // 2. Filter by planIds (if planIds is empty, allow all; else must match)
  filteredPreTicks = filteredPreTicks.filter(addon => {
    if (
      !planId ||
      !addon.planIds ||
      !Array.isArray(addon.planIds) ||
      addon.planIds.length === 0
    )
      return true;
    return addon.planIds
      .map(p => (typeof p === 'string' ? p.trim() : ''))
      .includes(typeof planId === 'string' ? planId.trim() : '');
  });

  // 3. Filter by phoneList if phoneSpecific is true (if false, allow all)
  filteredPreTicks = filteredPreTicks.filter(addon => {
    if (!addon.phoneSpecific) return true;
    if (
      !selectedProduct ||
      !addon.phoneList ||
      !Array.isArray(addon.phoneList) ||
      addon.phoneList.length === 0
    )
      return false;
    return addon.phoneList
      .map(p => (typeof p === 'string' ? p.trim() : ''))
      .includes(
        typeof selectedProduct === 'string' ? selectedProduct.trim() : ''
      );
  });

  // Flatten and filter based on transaction type if set
  return filteredPreTicks.flatMap(addon => {
    if (!addon.transactionSpecific) {
      return (addon.variationIds || []).map(variantId => ({
        productId: addon.productId,
        variantId
      }));
    } else {
      const key = typeMap[transactionType];
      const variantIds = addon.transactionVariationIds?.[key] || [];
      return (variantIds || []).map(variantId => ({
        productId: addon.productId,
        variantId
      }));
    }
  });
};

interface AddonVariant {
  id: string;
  name: string;
  duration?: number;
  discounts?: Array<{ id: string; name: string }>;
  payable?: string;
  priceAfterBundleItemDiscount?: string;
  discountBeforeBundleItems?: string;
  tagline?: string;
  price?: string;
  recurrable?: boolean;
}

interface ExistingAddon {
  id: string;
  name: string;
  variant: AddonVariant; // for single variant case
}

type AllAddons = {
  id: string;
  name: string;
  variants: AddonVariant[];
};

type FilteredAddon = {
  id: string;
  name: string;
  variant: AddonVariant;
};

/**
 * Creates a Set of existing addon keys for fast lookup
 * @param existingAddons - Array of existing addons
 * @returns Set of addon keys in format "productId::variantId"
 */
export const createExistingAddonKeysSet = (
  existingAddons: ExistingAddon[]
): Set<string> => {
  if (!existingAddons?.length) {
    return new Set();
  }
  return new Set(
    existingAddons.map(addon => `${addon.id}::${addon.variant.id}`)
  );
};

/**
 * Filters and flattens addon variants based on AEM pre-tick configuration and existing addons
 * @param allAddons - All available addons
 * @param pxAEMPreTickNormalized - Normalized AEM pre-tick configuration
 * @param existingAddonKeys - Set of existing addon keys
 * @returns Filtered and flattened addon variants
 */
export const filterAndFlattenAddonVariants = (
  allAddons: AllAddons[],
  pxAEMPreTickNormalized: Array<{ productId: string; variantId: string }>,
  existingAddonKeys: Set<string>
): FilteredAddon[] => {
  return allAddons.flatMap(addon =>
    (addon.variants || [])
      .filter(
        variant =>
          pxAEMPreTickNormalized.some(
            pre => pre.productId === addon.id && pre.variantId === variant.id
          ) && !existingAddonKeys.has(`${addon.id}::${variant.id}`)
      )
      .map(variant => ({
        id: addon.id,
        name: addon.name,
        variant
      }))
  );
};

/**
 * Transforms filtered variants into the format required for calculate payable API
 * @param filteredVariants - Filtered addon variants
 * @returns Array of addons in calculate payable format
 */
export const transformVariantsForCalculatePayable = (
  filteredVariants: FilteredAddon[]
): APP_TYPE_ANY[] => {
  return filteredVariants.map(addon => {
    const variant = addon.variant;
    return {
      id: variant?.id,
      productId: addon.id,
      action: 'add',
      quantity: 1,
      recurrable: variant?.recurrable,
      items: []
    };
  });
};

/**
 * Merges pricing information with addon variants
 * @param filteredVariants - Filtered addon variants
 * @param addonsPriceInfo - Pricing information from API
 * @returns Merged addons with pricing information
 */
// Define the type for a variant item in addonsPriceInfo
interface AddonsPriceInfoVariant {
  id: string;
  productId: string;
  action: string;
  quantity: number;
  recurrable: boolean;
  price: string;
  saving: string;
  savings?: Array<{
    id: string;
    name: string;
    amount: string;
  }>;
  refund: string;
  penalty: string;
  credit: string;
  payable: string;
  tax: string;
  // Add any other fields as needed
}

interface AddonsPriceInfo {
  id: string;
  price: string;
  saving: string;
  refund: string;
  penalty: string;
  credit: string;
  payable: string;
  tax: string;
  variants: AddonsPriceInfoVariant[];
  updatedAt: string;
}

export const mergeAddonsWithPricing = (
  filteredVariants: FilteredAddon[],
  addonsPriceInfo: AddonsPriceInfo
): FilteredAddon[] => {
  // Create a map for fast lookup of pricing info by variant id
  const pricingMap = new Map(
    (addonsPriceInfo?.variants ?? []).map((item: AddonsPriceInfoVariant) => [
      item.id,
      item
    ])
  );

  // Merge pricing info into each variant
  return filteredVariants.map(addon => {
    const variantId = addon.variant?.id;
    const pricingInfo = pricingMap.get(variantId) || {};
    return {
      id: addon.id,
      name: addon.name,
      variant: {
        ...addon.variant,
        ...(typeof pricingInfo === 'object' ? pricingInfo : {})
      }
    };
  });
};

export const isValidArrayWithData = <T = unknown>(arr: T): boolean => {
  return Array.isArray(arr) && arr != null && arr.length > 0;
};

/**
 * Formats service number for PX Addons Calculate Payable API by prepending '65'
 * @param serviceNumber - The service number to format (without '65' prefix)
 * @returns Formatted service number with '65' prefix, or null if invalid
 */
export const formatServiceNumberForPxAddons = (
  serviceNumber: string | number | null | undefined
): string | null => {
  // Handle null, undefined, or empty values
  if (
    serviceNumber === null ||
    serviceNumber === undefined ||
    serviceNumber === ''
  ) {
    return null;
  }

  // Convert to string if number and trim whitespace
  const serviceNumberStr =
    typeof serviceNumber === 'number'
      ? serviceNumber.toString()
      : String(serviceNumber).trim();

  // Check if empty after trimming
  if (serviceNumberStr === '') {
    return null;
  }

  // Prepend '65' to the service number
  return `65${serviceNumberStr}`;
};

export const getBtlCampignID = (): string => {
  const urlParams = getUrlParams(window.location.search);
  const urlCampaignID = urlParams[CONSTANTS.URL_PARAMS.BTL_CAMPAIGN_ID];
  return urlCampaignID || '';
};

export const formatBTLPrice = ({
  price,
  decimal = 2,
  currency = '$',
  suffix = ''
}: {
  price: string | number;
  decimal?: number;
  currency?: string;
  suffix?: string;
}): string => {
  if (price === undefined) return '';
  price = Number(price);
  const rootValue = Math.abs(price).toFixed(decimal);
  return `${price < 0 ? '-' : ''}${currency}${rootValue}${suffix}`;
};

export const openInNewTab = (url: string): void => {
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const toPlainText = html =>
  html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getBTLLayoutModel = (
  selectedPlan,
  btlOfferDetail
): BTLActionLayoutModel[] => {
  const layoutModel: BTLActionLayoutModel[] = [];
  if (!btlOfferDetail || !selectedPlan) return layoutModel;

  const mobileData = btlOfferDetail?.mobile[0];

  const displayItems = mobileData?.displayItems;

  const { content1 } = parsePlanDescription(selectedPlan?.planDescSubLiner);
  const contentWithIconList = content1.map(item => ({
    icon: item.iconName,
    text: toPlainText(item.text),
    alignItems: 'center'
  }));

  const description = displayItems?.description || '';
  const descriptionList = description.split(';');

  layoutModel.push({
    id: selectedPlan?.planId || '',
    title: displayItems?.title || selectedPlan?.planName || '',
    subtitle: selectedPlan?.sellingMonthlyCharges,
    ribbonText: '',
    selectedContent: {
      duration: getDurationLiner(mobileData) || '',
      planName: displayItems?.title || selectedPlan?.planName || '',
      currentPrice:
        formatBTLPrice({
          price: mobileData?.price?.offerPrice,
          suffix: '/mth'
        }) || '',
      originalPrice:
        mobileData?.plan?.offerPriceDuration === '0'
          ? formatBTLPrice({
              price: mobileData?.price?.normalPrice,
              suffix: '/mth'
            })
          : '',
      planImage: displayItems?.imageURL,
      planImageAlt: selectedPlan?.planName,
      iconWithTextContent: contentWithIconList || [],
      bulletlistTitle: 'Comes with',
      bulletlistContent: descriptionList || [],
      subscriptionsTitle: '',
      subscriptions: []
    }
  });

  return layoutModel;
};

const getContractText = (contract = '') => {
  const contractTxt = ('' + contract).trim();
  return !contractTxt || contractTxt === '0' ? 'No Contract' : `Renews yearly`;
};

const getAddonGroupImagename = (addon, addonGroupInfoLookup) => {
  if (!addon || !addonGroupInfoLookup) return null;
  const groupInfo = addonGroupInfoLookup[addon?.groupName];
  return groupInfo?.icon /*|| groupInfo?.iconImage*/;
};

const getAddonPrice = price => {
  if (!price) return '0';
  const priceStr = String(price).trim();
  return priceStr.startsWith('$') ? priceStr.substring(1) : priceStr;
};

export const getBTLOfferAddons = (
  filteredBTLAddons,
  addonGroupInfoLookup
): ServiceAddonType[] => {
  if (!filteredBTLAddons || filteredBTLAddons.length === 0) return [];

  const addonsWithImage = filteredBTLAddons.map(addon => ({
    ...(addon || []),
    iconImage:
      getAddonGroupImagename(addon, addonGroupInfoLookup) || addon?.icon
  }));

  return addonsWithImage.map(addon => ({
    categoryId: addon?.id,
    category: addon?.groupName || '',
    id: addon?.id,
    title: addon?.productTitle || '',
    usualMonthlyPrice: null,
    actualMonthlyPrice: getAddonPrice(addon?.price),
    image:
      addon?.iconImage && process.env.GATSBY_AEM_CDN_URL + addon?.iconImage,
    promoText: addon?.productDescription || null,
    description: addon?.productDescription || null,
    contractTerm: getContractText(addon?.contract),
    groupAddons: null,
    oneTimePayment: addon?.onetimePrice || '0',
    footerLiner: '',
    price: getAddonPrice(addon?.price),
    addonBoid: addon?.productSpecContainmentID,
    loading: addon?.isPretick ? 'success' : 'failure'
  }));
};

export const getSelectedAddonBoidList = (
  selectedBTLAddons: ServiceAddonType[]
): string[] => {
  if (!Array.isArray(selectedBTLAddons) || selectedBTLAddons.length === 0)
    return [];

  return selectedBTLAddons
    .filter(addon => addon?.loading === 'success')
    .map(addon => addon?.addonBoid)
    .filter(boid => !!boid);
};

export const getBTLAddonCategoryList = uniqueBTLVasIds => {
  if (!Array.isArray(uniqueBTLVasIds) || uniqueBTLVasIds.length === 0)
    return null;

  const uniquePxAddonList = uniqueBTLVasIds.filter(
    ({ BOID = '', discountBOID = '' }) =>
      BOID.includes('-') && discountBOID !== ''
  );
  const uniqueNonPXAddonList = uniqueBTLVasIds.filter(
    id => !id?.BOID.includes('-')
  );

  return {
    uniqueNonPXAddonList,
    uniquePxAddonList
  };
};

export const mergeBTLAddonsWithPricing = (
  filterAddon,
  addonsPriceInfo: AddonsPriceInfo
): FilteredAddon[] => {
  // Create a map for fast lookup of pricing info by variant id
  const pricingMap = new Map(
    (addonsPriceInfo?.variants ?? []).map((item: AddonsPriceInfoVariant) => [
      item.id,
      item
    ])
  );

  // Merge pricing info into each variant
  return filterAddon.map(addon => {
    const variantId = addon.variant?.id;
    const pricingInfo = pricingMap.get(variantId) || {};

    return {
      id: addon.id,
      name: addon.name,
      variant: {
        ...addon.variant,
        ...(typeof pricingInfo === 'object' ? pricingInfo : {})
      }
    };
  });
};

const isDynamicPxAddon = btlAddon =>
  'productId' in btlAddon || 'variantId' in btlAddon;

const normalizePxAddon = btlAddon =>
  isDynamicPxAddon(btlAddon)
    ? {
        variantId: btlAddon.variantId,
        productId: btlAddon.productId,
        type: btlAddon?.productName || null
      }
    : {
        variantId: btlAddon.BOID,
        productId: btlAddon.discountBOID,
        type: btlAddon?.type || null
      };

export const getPayloadForCalculatePayable = (
  pxAllAddons,
  pxExistingAddons,
  btlPXAddons
) => {
  const normalizedAddons = btlPXAddons.map(normalizePxAddon);

  const existingProductIds = new Set(pxExistingAddons.map(addon => addon?.id));
  const existingVariantIds = new Set(
    pxExistingAddons.map(addon => addon?.variant?.id)
  );

  const nonIntersectedAddons = normalizedAddons.filter(
    btlAddon =>
      !existingProductIds.has(btlAddon?.productId) ||
      !existingVariantIds.has(btlAddon?.variantId)
  );

  const matchedAddon = nonIntersectedAddons.reduce((result, btl) => {
    const product = pxAllAddons.find(addon => addon?.id === btl?.variantId);
    if (!product) return result;

    const matchedVariant = product.variants.find(v => v.id === btl.productId);

    if (!matchedVariant) return result;

    result.push({
      id: product.id,
      name: product.name,
      variant: matchedVariant,
      _btlSource: btl
    });
    return result;
  }, []);

  return {
    filterAddon: matchedAddon,
    requestPayload: transformVariantsForCalculatePayable(matchedAddon)
  };
};

export const handleByPassPXAddon = pxAddonResponse => {
  const errorResponse = pxAddonResponse?.error?.data;
  if (errorResponse) {
    return true;
  }
};

export const capitalizeFirstChar = (word: string): string => {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const logoutOnePass = async (
  callback?: () => void,
  errorCallback?: () => void
) => {
  const uiamAccessToken = getCookie(CONSTANT.UIAM_ACCESS_TOKEN);
  try {
    const result = await revokeTokenApi(uiamAccessToken);
    if (result?.code === CONSTANT.REVOKE_SUCCESS) {
      deleteCookie(CONSTANT.UIAM_ACCESS_TOKEN);
      if (typeof callback === 'function') {
        callback();
      }
    }
  } catch (e) {
    if (typeof errorCallback === 'function') {
      errorCallback();
    }
  }
};

export const parsePromotionPricingConfig = (
  offerMode: string,
  promotionPricingConfig: string
): {
  pricingMap: Array<{ planId: string; price: number }>;
  transactionOptions: string[];
} | null => {
  if (!offerMode || !promotionPricingConfig) {
    return null;
  }
  const trimmedOfferMode = offerMode.trim();
  // Config may contain multiple promotions separated by '||'.
  // Within each promotion: promotionId | transactionOptions | pricingList, separated by '|'.
  const promotionBlocks = promotionPricingConfig.split('||');
  for (const block of promotionBlocks) {
    const [promotionId, optionsString, pricingString] = block.split('|');
    if (!promotionId || !optionsString || !pricingString) continue;

    if (promotionId.trim() !== trimmedOfferMode) continue;
    try {
      const transactionOptions = optionsString
        .split(',')
        .map(opt => opt.trim())
        .filter(Boolean);

      const pricingMap = pricingString.split(',').map(pair => {
        const [planId, priceStr] = pair.split(':');
        return {
          planId: planId.trim(),
          price: parseFloat(priceStr.trim())
        };
      });

      if (pricingMap.some(p => isNaN(p.price))) {
        return null;
      }
      return { pricingMap, transactionOptions };
    } catch (e) {
      return null;
    }
  }

  return null;
};

export const isPromotionPlan = (promotion, selectedPlan) => {
  return (
    !!selectedPlan?.planId &&
    promotion?.promotionLoaded &&
    promotion?.promotionsPlansList?.some(
      ({ planId }) => planId === selectedPlan.planId
    )
  );
};

export const handlePromotionPlanNavigation = ({
  promotions,
  selectedPlan,
  navigate,
  onOpenModal
}) => {
  const isPromoPlan = isPromotionPlan(promotions, selectedPlan);
  const tabOptions = promotions?.transactionOptions || [];

  if (!isPromoPlan || tabOptions.length > 1) {
    onOpenModal(true);
    return;
  }

  const matchedTab = NUMBER_SELECTION_TAB_ITEMS.find(
    ({ ref }) => ref === tabOptions[0]
  );

  if (matchedTab) {
    navigate(`/number-selection${matchedTab.value}`);
  }
};
